import { UserRole } from '../../../types/UserRole';
import { type BiddingInfoResult } from '../../../types';
import { 
    formatJSTWithMillis, 
    calculateTotalWeight, 
    getCaltivationLabel, 
    getProductionLabel,
    getDisplayBidResultText
  } from './biddingFormatHelpers';
import { UserPermission } from '../../../types/UserPermission';

// ==========================================
// 1. ヘッダーの定数定義
// ==========================================
const CSV_HEADERS = {
  BUYER: ['入札日', '入札No.'
    , '売手コード', '売手名', '生産者コード', '生産者名'
    , '荷印', '茶期', '茶種', '品種名'
    , '最低落札値(円)'
    , 'GAP区分', '栽培区分', '生産履歴'
    , '容量①(kg)', '本数①(本)', '容量②(kg)', '本数②(本)', '総量(kg)'
    , '備考'
    , '入札額'
    , '最高落札値(円)'
    , '落札結果'
    ],
  SELLER: ['入札日', '入札No.'
    , '売手コード', '売手名', '生産者コード', '生産者名'
    , '荷印', '茶期', '茶種', '品種名'
    , '最低落札値(円)'
    , 'GAP区分', '栽培区分', '生産履歴'
    , '容量①(kg)', '本数①(本)', '容量②(kg)', '本数②(本)', '総量(kg)'
    , '備考'
    , '落札者名', '最高落札値(円)', '落札結果'
  ],
  ADMIN: ['入札日', '入札No.'
    , '売手コード', '売手名', '生産者コード', '生産者名'
    , '荷印'
    , '茶期', '茶種', '品種名'
    , '最低落札値(円)'
    , 'GAP区分', '栽培区分', '生産履歴'
    , '容量①(kg)', '本数①(本)', '容量②(kg)', '本数②(本)', '総量(kg)'
    , '備考'
    , '入札日履歴行番号'
    , '買手コード', '買手名'
    , '入札本数①(本)', '入札本数②(本)', '入札総量(kg)'
    , '入札値(円)'
    , '結果'
    , '入札日時'
    ],
} as const;

// ==========================================
// 2. ロール別のデータ生成関数 (切り出し)
// ==========================================
/** 買手用のデータ生成 */
const generateBuyerRows = (items: BiddingInfoResult[]): string[][] => {
  const rows: string[][] = [];
  items.forEach(item => {
    const totalAmount = calculateTotalWeight(item.capacity1, item.quantity1, item.capacity2, item.quantity2);

    // 落札結果の表示ロジック
    const displayResult = getDisplayBidResultText(item, UserRole.Buyer);
    // 生産者名の表示ロジック
    const displayFarmerName = item.isFarmerHidden ? '' : (item.farmerName ?? '');

    rows.push([
      item.biddingDate ?? '',                        // 入札日
      String(item.biddingNumber ?? ''),              // 入札No.
      String(item.sellerCode ?? ''),                 // 売手コード
      item.sellerName ?? '',                         // 売手名
      String(item.farmerCode ?? ''),                 // 生産者コード
      displayFarmerName,                             // 生産者名
      item.brand ?? '',                              // 荷印
      item.teaSeasonName ?? '',                      // 茶期
      item.teaTypeName ?? '',                        // 茶種
      item.varietyName ?? '',                        // 品種名
      String(item.bidMinPrice ?? 0),                 // 最低落札値
      item.gapName ?? '',                            // GAP区分
      getCaltivationLabel(item.caltivationCategory), // 栽培区分
      getProductionLabel(item.productionHistory),    // 生産履歴
      String(item.capacity1 ?? 0),                   // 容量①(kg)
      String(item.quantity1 ?? 0),                   // 本数①(本)
      String(item.capacity2 ?? 0),                   // 容量②(kg)
      String(item.quantity2 ?? 0),                   // 本数②(本)
      String(totalAmount),                           // 総量(kg)
      item.biko ?? '',                               // 備考      
      String(item.myBidPrice ?? 0),                  // 入札額
      String(item.highestBidPrice ?? 0),             // 最高落札値
      displayResult,                                 // 落札結果
    ]);
  });
  return rows;
};

/** 売手・生産者・JA用のデータ生成 */
const generateSellerRows = (items: BiddingInfoResult[]): string[][] => {
  const rows: string[][] = [];
  items.forEach(item => {
    const totalAmount = calculateTotalWeight(item.capacity1, item.quantity1, item.capacity2, item.quantity2);

    // 落札結果の表示ロジック
    let result = item.resultMsg ?? '';
    let displayPrice = String(item.highestBidPrice ?? '');
    if (result === "" || result === "不成立")
      {
        displayPrice = "";
        result = "";
      }

    // 生産者名の表示ロジック
    //const displayFarmerName = item.isFarmerHidden ? '' : (item.farmerName ?? '');

    rows.push([
      item.biddingDate ?? '',                        // 入札日
      String(item.biddingNumber ?? ''),              // 入札No.
      String(item.sellerCode ?? ''),                 // 売手コード
      item.sellerName ?? '',                         // 売手名
      String(item.farmerCode ?? ''),                 // 生産者コード
      String(item.farmerName ?? ''),                 // 生産者名
      item.brand ?? '',                              // 荷印
      item.teaSeasonName ?? '',                      // 茶期
      item.teaTypeName ?? '',                        // 茶種
      item.varietyName ?? '',                        // 品種名
      String(item.bidMinPrice ?? ''),                // 最低落札値
      item.gapName ?? '',                            // GAP区分
      getCaltivationLabel(item.caltivationCategory), // 栽培区分
      getProductionLabel(item.productionHistory),    // 生産履歴
      String(item.capacity1 ?? 0),                   // 容量①(kg)
      String(item.quantity1 ?? 0),                   // 本数①(本)
      String(item.capacity2 ?? 0),                   // 容量②(kg)
      String(item.quantity2 ?? 0),                   // 本数②(本)
      String(totalAmount),                           // 総量(kg)
      item.biko ?? '',                               // 備考
      String(item.highestBidBuyerName ?? ''),        // 落札者名
      displayPrice,                                  // 最高落札値(円)
      result,                                        // 落札結果
    ]);
  });
  return rows;
};

/** 市場用のデータ生成 */
const generateAdminRows = (items: BiddingInfoResult[]): string[][] => {
  const rows: string[][] = [];
  items.forEach(item => {
    const totalAmount = calculateTotalWeight(
        item.capacity1, item.quantity1, 
        item.capacity2, item.quantity2
      );

      // 共通親データ
      const baseItemData = [
        item.biddingDate ?? '',                        // 入札日
        String(item.biddingNumber ?? ''),              // 入札No.
        String(item.sellerCode ?? ''),                 // 売手コード
        item.sellerName ?? '',                         // 売手名
        String(item.farmerCode ?? ''),                 // 生産者コード
        item.farmerName ?? '',                         // 生産者名
        item.brand ?? '',                              // 荷印
        item.teaSeasonName ?? '',                      // 茶期
        item.teaTypeName ?? '',                        // 茶種
        item.varietyName ?? '',                        // 品種名
        String(item.bidMinPrice ?? 0),                 // 最低落札値
        item.gapName ?? '',                            // GAP区分
        getCaltivationLabel(item.caltivationCategory), // 栽培区分
        getProductionLabel(item.productionHistory),    // 生産履歴
        String(item.capacity1 ?? 0),                   // 容量①(kg)
        String(item.quantity1 ?? 0),                   // 本数①(本)
        String(item.capacity2 ?? 0),                   // 容量②(kg)
        String(item.quantity2 ?? 0),                   // 本数②(本)
        String(totalAmount),                           // 総量(kg)
        item.biko ?? ''                                // 備考
      ];

      // 入札履歴がない場合
      if (!item.biddingInfoDatas || item.biddingInfoDatas.length === 0) {
        rows.push([
          ...baseItemData,
          '', '', '', '', '', '', '',
          item.resultMsg ?? '',
          '',
        ]);
      } 
      // 入札履歴がある場合
      else {
        item.biddingInfoDatas.forEach((info, index) => {

        const bidTotalWeight = calculateTotalWeight(
            item.capacity1, info.quantity1,
            item.capacity2, info.quantity2
            );
            
          let historyResultText = '';
          if (info.isWinningBid) {
            historyResultText = item.resultMsg ?? '';
          } else if (info.isDisabled) {
            historyResultText = '無効';
          }

          rows.push([
            ...baseItemData,
            String(index + 1),                     // 入札日履歴行番号
            String(info.buyerCode ?? ''),          // 買手コード
            info.buyerName ?? '',                  // 買手名
            String(info.quantity1 ?? 0),           // 入札本数①(本)
            String(info.quantity2 ?? 0),           // 入札本数②(本)
            String(bidTotalWeight),                // 入札総量(kg)
            String(info.bidPrice ?? 0),            // 入札値
            historyResultText,                     // 結果
            formatJSTWithMillis(info.bidTimestamp) // 入札日時
          ]);
        });
      }
  });
  return rows;
};

// ==========================================
// 3. メインの変換ロジック
// ==========================================
export const generateBiddingCsvData = (items: BiddingInfoResult[], role: UserRole, permission: UserPermission): string[][] => {
  if (role === UserRole.Buyer) {
    return [[...CSV_HEADERS.BUYER], ...generateBuyerRows(items)];
  } 
  
  if (role === UserRole.Seller || role === UserRole.Farmer || role === UserRole.JA) {
    return [[...CSV_HEADERS.SELLER], ...generateSellerRows(items)];
  } 

  console.log(permission);
  const userPerm = Number(permission) as UserPermission;

  if (role === UserRole.Admin && (userPerm === UserPermission.Editor || userPerm === UserPermission.Viewer)){
    return [[...CSV_HEADERS.SELLER], ...generateSellerRows(items)];
  }
  
  return [[...CSV_HEADERS.ADMIN], ...generateAdminRows(items)];
};